/**
 * Fill-campaign math + the panel↔canonical axis mapping (FXC-9009 follow-on).
 *
 * Pure, unit-tested helpers behind FillPanel — everything here is a function of its inputs so
 * the value-strip numbers ("N adaptive vs 5^k dense", "×2.8 warm-start savings") are honest,
 * reproducible claims rather than render-time arithmetic scattered through JSX.
 *
 * FILL_AXES is THE single source of truth for how the six workbench axes (SweepPanel's
 * vocabulary: deploy/flaperon/alpha/mach/re/disk_loading) map onto the composite_db schema's
 * canonical axis names (thread-api composite_db/schema.py AXES_BY_NAME). The campaign wire
 * speaks CANONICAL names end-to-end — bounds, hold pins, iteration conditions — so the panel
 * converts exactly once, here. Two conversions matter:
 *
 *   - re: shown/boxed in MILLIONS (like SweepPanel); the canonical `re_chord_millions` axis is
 *     ALSO in millions, so the box passes through unchanged and only the RUNNER converts back
 *     to the raw `reynolds` field on each solve point (× 1e6).
 *   - deploy/flaperon are GEOMETRY axes: each distinct value needs its own re-mesh, so they may
 *     only be RANGE axes on a local-solve host (the server 400s otherwise; the panel locks them
 *     to hold, mirroring SweepPanel's cloud-only lock).
 */
import type { CampaignAxisSpec, CampaignDetail } from "../../../api/highliftCampaign";

/** Raw chord Reynolds <-> millions (the canonical `re_chord_millions` axis is in millions). */
const RE_MILLION = 1e6;

/** The six workbench operating axes (panel vocabulary). Lived in SweepPanel
 *  until the two panels merged into the one AxisTable (FXC-9009 phase 5) —
 *  the shared vocabulary now lives with the shared axis math. */
export type SweepAxisId = "deploy" | "flaperon" | "alpha" | "mach" | "re" | "disk_loading";

/** The live held values for any axis not being swept/ranged. */
export interface SweepLiveValues {
  deploy: number;
  flaperon: number;
  alpha: number;
  mach: number;
  reynolds: number;
  temperature: number;
  /** Actuator-disk thrust loading Δp/q∞ — the live held blowing value (FXC-9009). */
  diskLoading: number;
}

export interface FillAxisDef {
  /** Panel axis id — SweepPanel's six-axis vocabulary, so the two panels read identically. */
  id: SweepAxisId;
  /** Canonical composite_db axis name — what the campaign wire (bounds/conditions) speaks. */
  canonical: string;
  label: string;
  /** Default RANGE box — the same [from, to] SweepPanel sweeps by default, so switching a
   *  row from Grid-sweep to Tailor-fill boxes the same span the user already knows. */
  range: [number, number];
  fmt: (v: number) => string;
  /** Needs a re-mesh per value → range-able only on a local-solve host (cloud-only locks it). */
  geometry: boolean;
}

/** The six fill axes, in SweepPanel row order. Ranges mirror SweepPanel's AXES defaults —
 *  keep the two lists in lockstep (campaignMath.test.ts pins the numbers). */
export const FILL_AXES: FillAxisDef[] = [
  { id: "deploy", canonical: "flap_deploy_deg", label: "deploy angle°", range: [0, 40], fmt: (v) => `${v.toFixed(0)}°`, geometry: true },
  { id: "flaperon", canonical: "flaperon_deflection_deg", label: "flaperon angle°", range: [0, 20], fmt: (v) => `${v.toFixed(0)}°`, geometry: true },
  { id: "alpha", canonical: "alpha_deg", label: "α (deg)", range: [-2, 16], fmt: (v) => `${v.toFixed(1)}°`, geometry: false },
  { id: "mach", canonical: "mach", label: "Mach", range: [0.15, 0.3], fmt: (v) => v.toFixed(2), geometry: false },
  { id: "re", canonical: "re_chord_millions", label: "Re (×10⁶)", range: [3, 15], fmt: (v) => `${v.toFixed(1)}M`, geometry: false },
  { id: "disk_loading", canonical: "disk_thrust_loading", label: "Blowing Δp/q∞", range: [0, 2.0], fmt: (v) => v.toFixed(2), geometry: false },
];

/** panel id → canonical axis name (derived from FILL_AXES so it can never drift). */
export const CANONICAL_AXIS_BY_PANEL: Record<SweepAxisId, string> = Object.fromEntries(
  FILL_AXES.map((ax) => [ax.id, ax.canonical]),
) as Record<SweepAxisId, string>;

/** canonical axis name → panel id (the reverse map, for reading campaign state back). */
export const PANEL_AXIS_BY_CANONICAL: Record<string, SweepAxisId> = Object.fromEntries(
  FILL_AXES.map((ax) => [ax.canonical, ax.id]),
);

/** The live held value of a panel axis in CANONICAL units (Re → millions; everything else
 *  is already canonical). This is what a "hold" axis pins the acquisition to. */
export function liveCanonicalValue(id: SweepAxisId, live: SweepLiveValues): number {
  switch (id) {
    case "deploy": return live.deploy;
    case "flaperon": return live.flaperon;
    case "alpha": return live.alpha;
    case "mach": return live.mach;
    case "re": return live.reynolds / RE_MILLION;
    case "disk_loading": return live.diskLoading;
  }
}

/** How many points a conventional dense grid would burn over the same box: `perAxisSteps`
 *  values on each RANGE axis, cartesian product → perAxisSteps^k. This is the denominator
 *  of the campaign's value claim ("24 adaptive points vs 125 dense-grid points"), so the
 *  formula is surfaced alongside the number — the reader can check the arithmetic. */
export function denseGridEquivalent(
  axes: CampaignAxisSpec[],
  perAxisSteps = 5,
): { rangeAxes: number; points: number; formula: string } {
  const rangeAxes = axes.filter((ax) => ax.mode === "range").length;
  const points = Math.pow(perAxisSteps, rangeAxes);
  return { rangeAxes, points, formula: `${perAxisSteps}^${rangeAxes} = ${points}` };
}

/** Box coverage 0..1 → a display percent (clamped; NaN/absent reads as 0). */
export function coveragePercent(coverage: number | null | undefined): number {
  const c = typeof coverage === "number" && Number.isFinite(coverage) ? coverage : 0;
  return Math.round(Math.max(0, Math.min(1, c)) * 100);
}

/** One step of the fill replay: a dispatched campaign point in chronological order,
 *  flattened to what the replay animation needs — the deploy angle it drives, the
 *  condition/CL it narrates, and the acquisition's verbatim rationale. */
export interface ReplayStep {
  iterationIndex: number;
  alphaDeg: number;
  deployDeg: number;
  /** Blowing Δp/q∞ (canonical disk_thrust_loading). */
  blowing: number;
  flaperonDeg: number;
  mach: number;
  /** Chord Reynolds in MILLIONS (display units, same as the panel). */
  reMillions: number;
  CL: number | null;
  backend: string | null;
  cached: boolean;
  failed: boolean;
  /** Acquisition rationale VERBATIM: per-point reasons when persisted, else the
   *  iteration's `reasons_top`, else the engine label — never invented. */
  reason: string;
  requestedAt: string;
  runPath: string | null;
  /** The point's own verbatim reasons (unsplit), when persisted — the LLM "why"
   *  narrator grounds on these; empty when only iteration-level reasons exist.
   *  Optional so existing ReplayStep test builders stay valid; campaignReplaySteps
   *  always populates it (>=[]). */
  reasons?: string[];
  /** Rich per-point acquisition signals for narration (FXC-9009). All optional:
   *  NEW campaigns persist them, the beecbb20 demo omits them (narration then
   *  reads from `reasons` alone). Never invented — straight off the point. */
  score?: number | null;
  purpose?: string | null;
  components?: Record<string, number> | null;
  classification?: string | null;
  disagreementZ?: number | null;
  /** Iteration provenance so narration can note coverage before/after. */
  engine?: string | null;
  coverageAfter?: number | null;
}

/** Flatten a campaign's iteration log into chronological replay steps.
 *
 *  Order is the runner's dispatch order: the single sequential runner APPENDS
 *  iterations, and points within one iteration are the batch in acquisition
 *  rank order. Pending points are skipped (nothing to show yet); failed points
 *  are KEPT and flagged — the replay stays honest about what the campaign
 *  actually spent. Condition axes a campaign held can be absent from the
 *  persisted condition; they fall back to the config's hold values (the same
 *  fallback the runner itself applies when building the solve point).
 */
export function campaignReplaySteps(campaign: CampaignDetail | null): ReplayStep[] {
  if (!campaign) return [];
  const holds: Record<string, number> = {};
  for (const ax of campaign.config?.axes ?? []) {
    if (ax.mode === "hold" && typeof ax.value === "number") holds[ax.axis] = ax.value;
  }
  const axisValue = (
    cond: Record<string, number | string>,
    key: string,
    fallback: number,
  ): number => {
    const v = cond[key];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    const held = holds[key];
    return held !== undefined ? held : fallback;
  };

  const steps: ReplayStep[] = [];
  for (const it of campaign.state?.iterations ?? []) {
    for (const p of it.points ?? []) {
      if (!p || p.status === "pending") continue;
      const cond = p.condition ?? {};
      const reason =
        (p.reasons && p.reasons.length ? p.reasons : it.reasons_top ?? []).join(" · ") ||
        it.engine ||
        "";
      steps.push({
        iterationIndex: it.index,
        alphaDeg: axisValue(cond, "alpha_deg", 0),
        deployDeg: axisValue(cond, "flap_deploy_deg", 0),
        blowing: axisValue(cond, "disk_thrust_loading", 0),
        flaperonDeg: axisValue(cond, "flaperon_deflection_deg", 0),
        mach: axisValue(cond, "mach", 0.2),
        reMillions: axisValue(cond, "re_chord_millions", 0),
        CL: typeof p.CL === "number" && Number.isFinite(p.CL) ? p.CL : null,
        backend: p.backend ?? null,
        cached: p.backend === "cache",
        failed: p.status === "failed",
        reason,
        requestedAt: it.requested_at,
        runPath: p.run_path ?? null,
        reasons: p.reasons && p.reasons.length ? p.reasons : it.reasons_top ?? [],
        score: typeof p.score === "number" ? p.score : null,
        purpose: p.purpose ?? null,
        components: p.components ?? null,
        classification: p.classification ?? null,
        disagreementZ: typeof p.disagreement_z === "number" ? p.disagreement_z : null,
        engine: it.engine ?? null,
        coverageAfter: typeof it.coverage_after === "number" ? it.coverage_after : null,
      });
    }
  }
  return steps;
}

/** One point of the N-D → 2D projection: raw view-axis coordinates plus how far
 *  the point sits OFF the chosen slice (0 = inside the slab, 1 = the far edge of
 *  the axis range). The projection panel ghosts by this — off-slice points stay
 *  visible but honestly faded, instead of being silently flattened onto the view. */
export interface ProjectedStep {
  step: ReplayStep;
  /** Raw values on the picked view axes. */
  x: number;
  y: number;
  /** 0..1 — max over non-viewed RANGE axes of the normalized distance beyond the
   *  slab. Held axes contribute 0 (every point sits exactly on a hold). */
  offSlice: number;
}

export interface ProjectionView {
  /** Canonical names of the two view axes. */
  x: string;
  y: string;
  /** Pinned value per non-viewed range axis (canonical name → value). */
  pins: Record<string, number>;
  /** Slab half-thickness as a fraction of each pinned axis's range (0..1). */
  slabFrac: number;
}

/** Read a canonical axis off a ReplayStep (the same table FillMap draws from). */
export const STEP_AXIS_VALUE: Record<string, (s: ReplayStep) => number> = {
  alpha_deg: (s) => s.alphaDeg,
  flap_deploy_deg: (s) => s.deployDeg,
  flaperon_deflection_deg: (s) => s.flaperonDeg,
  mach: (s) => s.mach,
  re_chord_millions: (s) => s.reMillions,
  disk_thrust_loading: (s) => s.blowing,
};

/** Project campaign steps onto a chosen 2-axis view of the N-D box.
 *
 *  Every axis the view does NOT show and the campaign RANGED contributes an
 *  off-slice distance: |value − pin| normalized by the axis's declared range,
 *  minus the slab (points inside the slab count as ON the slice). The result's
 *  `offSlice` is the max over those axes — the single honest "how far from the
 *  view you asked for" number the panel fades by. Held axes never contribute
 *  (the campaign pinned them physically, not visually).
 */
export function projectStepsToView(
  steps: ReplayStep[],
  axes: CampaignAxisSpec[],
  view: ProjectionView,
): ProjectedStep[] {
  const xGet = STEP_AXIS_VALUE[view.x];
  const yGet = STEP_AXIS_VALUE[view.y];
  if (!xGet || !yGet) return [];
  const pinnedAxes = axes.filter(
    (ax) =>
      ax.mode === "range" &&
      ax.axis !== view.x &&
      ax.axis !== view.y &&
      typeof ax.min === "number" &&
      typeof ax.max === "number" &&
      (ax.max as number) > (ax.min as number) &&
      STEP_AXIS_VALUE[ax.axis] != null,
  );
  const slab = Math.max(0, Math.min(1, view.slabFrac));
  return steps.map((step) => {
    let offSlice = 0;
    for (const ax of pinnedAxes) {
      const lo = ax.min as number;
      const hi = ax.max as number;
      const pin = view.pins[ax.axis] ?? (lo + hi) / 2;
      const dist = Math.abs(STEP_AXIS_VALUE[ax.axis]!(step) - pin) / (hi - lo);
      const beyond = Math.max(0, dist - slab);
      const denom = Math.max(1e-9, 1 - slab);
      offSlice = Math.max(offSlice, Math.min(1, beyond / denom));
    }
    return { step, x: xGet(step), y: yGet(step), offSlice };
  });
}

/** NN warm-start savings: seeding each RANS solve from the flowfield-NN's predicted field is
 *  validated at ~2.8× fewer solver steps to the SAME converged CL (FXC-9009 warm-start study,
 *  scripts/warmstart/*) — i.e. every warm-started run saved ≈ 1 − 1/2.8 ≈ 64% of its steps. */
export function warmStartSavings(
  warmStarted: number,
  factor = 2.8,
): { stepsSavedPct: number; label: string } {
  const stepsSavedPct = 1 - 1 / factor;
  return {
    stepsSavedPct,
    label: `${warmStarted} warm-started · ×${factor} fewer steps each (≈${Math.round(stepsSavedPct * 100)}% solver work saved)`,
  };
}
